import { morph } from '@theme/morph';
import { Component } from '@theme/component';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, isMobileBreakpoint, getIOSVersion } from '@theme/utilities';

export class QuickAddComponent extends Component {
  /** @type {AbortController | null} */
  #abortController = null;
  /** @type {Map<string, Element>} */
  #cachedContent = new Map();
  /** @type {boolean} */
  #implantSelectorCssLoaded = false;

  get productPageUrl() {
    // First check for data-product-url attribute (used in list view)
    const dataProductUrl = this.dataset.productUrl;
    if (dataProductUrl) {
      return dataProductUrl;
    }
    
    // Fallback to product-card method (used in grid view)
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    const productLink = productCard?.getProductCardLink();

    if (!productLink?.href) return '';

    const url = new URL(productLink.href);

    if (url.searchParams.has('variant')) {
      return url.toString();
    }

    const selectedVariantId = this.#getSelectedVariantId();
    if (selectedVariantId) {
      url.searchParams.set('variant', selectedVariantId);
    }

    return url.toString();
  }

  /**
   * Gets the currently selected variant ID from the product card
   * @returns {string | null} The variant ID or null
   */
  #getSelectedVariantId() {
    const productCard = /** @type {import('./product-card').ProductCard | null} */ (this.closest('product-card'));
    return productCard?.getSelectedVariantId() || null;
  }

  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#closeQuickAddModal);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    mediaQueryLarge.removeEventListener('change', this.#closeQuickAddModal);
    this.#abortController?.abort();
  }

  /**
   * Handles quick add button click
   * @param {Event} event - The click event
   */
  handleClick = async (event) => {
    event.preventDefault();

    const currentUrl = this.productPageUrl;

    // Check if we have cached content for this URL
    let productGrid = this.#cachedContent.get(currentUrl);
    let hasImplantSelector = false;
    /** @type {Document | null} */
    let fetchedHtml = null;

    if (!productGrid) {
      // Fetch and cache the content
      fetchedHtml = await this.fetchProductPage(currentUrl);
      if (fetchedHtml) {
        // Check if product has implant-selector-component
        const implantSelector = fetchedHtml.querySelector('implant-selector-component');
        if (implantSelector) {
          hasImplantSelector = true;
          // Create a wrapper for the implant selector content
          const wrapper = document.createElement('div');
          wrapper.classList.add('quick-add-modal__implant-content');
          wrapper.setAttribute('data-has-implant-selector', 'true');
          
          // Add product title
          const productTitle = fetchedHtml.querySelector('.view-product-title a, h1');
          if (productTitle) {
            const titleWrapper = document.createElement('div');
            titleWrapper.classList.add('quick-add-modal__product-title');
            const titleLink = document.createElement('a');
            titleLink.href = this.productPageUrl;
            titleLink.textContent = productTitle.textContent || this.dataset.productTitle || '';
            titleWrapper.appendChild(titleLink);
            wrapper.appendChild(titleWrapper);
          }
          
          // Clone the implant-selector-component directly (it has the .implant-selector class and inline styles)
          wrapper.appendChild(implantSelector.cloneNode(true));
          
          productGrid = wrapper;
          this.#cachedContent.set(currentUrl, productGrid);
          
          // Load CSS for implant-selector from the fetched HTML (JS is inline in Liquid)
          this.#loadImplantSelectorCss(fetchedHtml);
        } else {
          const gridElement = fetchedHtml.querySelector('[data-product-grid-content]');
          if (gridElement) {
            // Cache the cloned element to avoid modifying the original
            productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
            this.#cachedContent.set(currentUrl, productGrid);
          }
        }
      }
    } else {
      // Check if cached content has implant selector
      hasImplantSelector = productGrid.hasAttribute('data-has-implant-selector');
      
      // If it has implant selector, ensure CSS is loaded (JS is inline in Liquid)
      if (hasImplantSelector) {
        this.#loadImplantSelectorCss();
      }
    }

    if (productGrid) {
      // Use a fresh clone from the cache
      const freshContent = /** @type {Element} */ (productGrid.cloneNode(true));
      await this.updateQuickAddModal(freshContent, hasImplantSelector);
    }

    this.#openQuickAddModal();
  };

  /** @param {QuickAddDialog} dialogComponent */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(DialogCloseEvent.eventName, () => this.toggleAttribute('stay-visible', false), {
      once: true,
    });
  }

  #openQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#stayVisibleUntilDialogCloses(dialogComponent);

    dialogComponent.showDialog();
  };

  #closeQuickAddModal = () => {
    const dialogComponent = document.getElementById('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    dialogComponent.closeDialog();
  };

  /**
   * Fetches the product page content
   * @param {string} productPageUrl - The URL of the product page to fetch
   * @returns {Promise<Document | null>}
   */
  async fetchProductPage(productPageUrl) {
    if (!productPageUrl) return null;

    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    try {
      const response = await fetch(productPageUrl, {
        signal: this.#abortController.signal,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product page: HTTP error ${response.status}`);
      }

      const responseText = await response.text();
      const html = new DOMParser().parseFromString(responseText, 'text/html');

      return html;
    } catch (error) {
      if (error.name === 'AbortError') {
        return null;
      } else {
        throw error;
      }
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * Re-renders the variant picker.
   * @param {Element} productGrid - The product grid element
   * @param {boolean} [hasImplantSelector=false] - Whether the product has an implant selector
   */
  async updateQuickAddModal(productGrid, hasImplantSelector = false) {
    const modalContent = document.getElementById('quick-add-modal-content');
    const dialogComponent = document.getElementById('quick-add-dialog');

    if (!productGrid || !modalContent) return;

    // Toggle implant selector mode on the dialog
    if (dialogComponent) {
      dialogComponent.classList.toggle('quick-add-dialog--implant', hasImplantSelector);
    }

    // Skip mobile reorganization for implant selector - it has its own layout
    if (hasImplantSelector) {
      morph(modalContent, productGrid);
      // Re-initialize the implant-selector-component after morph
      await this.#reinitializeImplantSelector(modalContent);
      return;
    }

    if (isMobileBreakpoint()) {
      const productDetails = productGrid.querySelector('.product-details');
      if (!productDetails) return;
      const productFormComponent = productGrid.querySelector('product-form-component');
      const variantPicker = productGrid.querySelector('variant-picker');
      const productPrice = productGrid.querySelector('product-price');
      const productTitle = document.createElement('a');
      productTitle.textContent = this.dataset.productTitle || '';

      // Make product title as a link to the product page
      productTitle.href = this.productPageUrl;

      if (!productFormComponent || !variantPicker || !productPrice || !productTitle) return;

      const productHeader = document.createElement('div');
      productHeader.classList.add('product-header');

      productHeader.appendChild(productTitle);
      productHeader.appendChild(productPrice);
      productGrid.appendChild(productHeader);
      productGrid.appendChild(variantPicker);
      productGrid.appendChild(productFormComponent);
      productDetails.remove();
    }

    morph(modalContent, productGrid);

    this.#syncVariantSelection(modalContent);
  }

  /**
   * Syncs the variant selection from the product card to the modal
   * @param {Element} modalContent - The modal content element
   */
  #syncVariantSelection(modalContent) {
    const selectedVariantId = this.#getSelectedVariantId();
    if (!selectedVariantId) return;

    // Find and check the corresponding input in the modal
    const modalInputs = modalContent.querySelectorAll('input[type="radio"][data-variant-id]');
    for (const input of modalInputs) {
      if (input instanceof HTMLInputElement && input.dataset.variantId === selectedVariantId && !input.checked) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
        break;
      }
    }
  }

  /**
   * Re-initializes the implant-selector-component after morph
   * If the custom element is not registered, load the JS file first
   * @param {Element} container - The container element with the implant-selector
   */
  async #reinitializeImplantSelector(container) {
    const implantSelector = container.querySelector('implant-selector-component');
    if (!implantSelector) return;
    
    // Check if the custom element is registered
    if (!customElements.get('implant-selector-component')) {
      // Load the JS file dynamically
      await this.#loadImplantSelectorJs();
      // Wait for the element to be defined
      await customElements.whenDefined('implant-selector-component');
    }
    
    // Force re-connection by removing and re-adding the element
    const parent = implantSelector.parentNode;
    if (parent) {
      const placeholder = document.createComment('implant-selector-placeholder');
      parent.replaceChild(placeholder, implantSelector);
      
      // Use setTimeout to ensure DOM is updated before re-adding
      setTimeout(() => {
        parent.replaceChild(implantSelector, placeholder);
      }, 0);
    }
  }

  /**
   * Loads the implant-selector JS module if not already loaded
   * @returns {Promise<void>}
   */
  async #loadImplantSelectorJs() {
    // Check if already defined
    if (customElements.get('implant-selector-component')) return;
    
    // Check if script is already in the document
    const existingScript = document.querySelector('script[src*="implant-selector.js"]');
    if (existingScript) return;
    
    // Construct the URL from existing asset URLs
    let jsUrl = null;
    const existingAssetScript = document.querySelector('script[src*="/assets/"]');
    if (existingAssetScript) {
      const src = existingAssetScript.getAttribute('src') || '';
      const assetsBase = src.substring(0, src.lastIndexOf('/assets/') + 8);
      jsUrl = `${assetsBase}implant-selector.js`;
    }
    
    if (jsUrl) {
      return new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.type = 'module';
        script.src = jsUrl;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load implant-selector.js'));
        document.head.appendChild(script);
      });
    }
  }

  /**
   * Loads the implant-selector CSS file if not already loaded
   * @param {Document} [fetchedHtml] - The fetched HTML document to extract CSS URL from
   */
  #loadImplantSelectorCss(fetchedHtml) {
    if (this.#implantSelectorCssLoaded) return;
    
    // Check if CSS is already in the document
    const existingLink = document.querySelector('link[href*="implant-selector.css"]');
    if (existingLink) {
      this.#implantSelectorCssLoaded = true;
      return;
    }
    
    // Try to get the CSS URL from the fetched HTML (search in head and body)
    let cssUrl = null;
    if (fetchedHtml) {
      // Search in head first
      const headLink = fetchedHtml.head?.querySelector('link[href*="implant-selector.css"]');
      if (headLink) {
        cssUrl = headLink.getAttribute('href');
      } else {
        // Fallback: search in entire document
        const anyLink = fetchedHtml.querySelector('link[href*="implant-selector.css"]');
        if (anyLink) {
          cssUrl = anyLink.getAttribute('href');
        }
      }
    }
    
    // If still no URL found, construct it from existing asset URLs
    if (!cssUrl) {
      const existingAssetLink = document.querySelector('link[href*="/assets/"]');
      if (existingAssetLink) {
        const href = existingAssetLink.getAttribute('href') || '';
        const assetsBase = href.substring(0, href.lastIndexOf('/assets/') + 8);
        cssUrl = `${assetsBase}implant-selector.css`;
      }
    }
    
    if (cssUrl) {
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = cssUrl;
      document.head.appendChild(link);
      this.#implantSelectorCssLoaded = true;
    }
  }

}

if (!customElements.get('quick-add-component')) {
  customElements.define('quick-add-component', QuickAddComponent);
}

class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.handleCartUpdate, { signal: this.#abortController.signal });
    this.addEventListener(ThemeEvents.variantUpdate, this.#updateProductTitleLink);

    this.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleDialogClose);
  }

  /**
   * Closes the dialog
   * @param {CartUpdateEvent} event - The cart update event
   */
  handleCartUpdate = (event) => {
    if (event.detail.data.didError) return;
    this.closeDialog();
  };

  #updateProductTitleLink = (/** @type {CustomEvent} */ event) => {
    const anchorElement = /** @type {HTMLAnchorElement} */ (
      event.detail.data.html?.querySelector('.view-product-title a')
    );
    const viewMoreDetailsLink = /** @type {HTMLAnchorElement} */ (this.querySelector('.view-product-title a'));
    const mobileProductTitle = /** @type {HTMLAnchorElement} */ (this.querySelector('.product-header a'));

    if (!anchorElement) return;

    if (viewMoreDetailsLink) viewMoreDetailsLink.href = anchorElement.href;
    if (mobileProductTitle) mobileProductTitle.href = anchorElement.href;
  };

  #handleDialogClose = () => {
    const iosVersion = getIOSVersion();
    /**
     * This is a patch to solve an issue with the UI freezing when the dialog is closed.
     * To reproduce it, use iOS 16.0.
     */
    if (!iosVersion || iosVersion.major >= 17 || (iosVersion.major === 16 && iosVersion.minor >= 4)) return;

    requestAnimationFrame(() => {
      /** @type {HTMLElement | null} */
      const grid = document.querySelector('#ResultsList [product-grid-view]');
      if (grid) {
        const currentWidth = grid.getBoundingClientRect().width;
        grid.style.width = `${currentWidth - 1}px`;
        requestAnimationFrame(() => {
          grid.style.width = '';
        });
      }
    });
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
